// src/components/forms/PrayerForm.tsx
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { PrayerFormProps, PrayerFormData, prayerSchema } from '@/components/prayers/PrayerFormProps';
import { useAuth } from '@/contexts/auth';
import { CustomCategoryService } from '@/services/CustomCategoryService';
import PrayerFormHeader from './components/PrayerFormHeader';
import CategorySelector from './components/CategorySelector';
import FormActions from './components/FormActions';
import EncouragementMessage from './components/EncouragementMessage';

export default function PrayerForm({
  onSubmit,
  isSubmitting = false,
  onCancel,
  showActions = true,
}: PrayerFormProps) {
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const { user } = useAuth();

  const form = useForm<PrayerFormData>({
    resolver: zodResolver(prayerSchema),
    defaultValues: { title: '', content: '', category: '', visibility: 'public' },
  });

  // Load user's categories on component mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) {
        setIsLoadingData(false);
        return;
      }

      try {
        const userCategories = await CustomCategoryService.getUserCategories(user.id);
        setCategories(userCategories);
      } catch (error) {
        console.error('Error loading user data:', error);
        toast.error('Failed to load categories');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadUserData();
  }, [user?.id]);

  const handleSubmit = async (data: PrayerFormData) => {
    console.log('Prayer form submit handler called with:', data);
    await onSubmit(data);
    form.reset();
  };

  const handleAddCategory = async (category: string) => {
    if (!user?.id) {
      toast.error('Please log in to add custom categories');
      return;
    }

    try {
      await CustomCategoryService.addCustomCategory(user.id, category);
      setCategories((prev) => [...prev, category]);
      toast.success('Category added successfully');
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    }
  };

  if (isLoadingData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center p-8">
          <div className="text-sm text-muted-foreground">Loading form data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PrayerFormHeader />

      <Form {...form}>
        <form
          id="prayer-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-6 mt-6"
        >
          {/* Prayer Title */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  Prayer Title (optional)
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    maxLength={100}
                    placeholder="Enter a meaningful title for your prayer (optional)..."
                    className="bg-background border-border text-foreground"
                  />
                </FormControl>
                <FormMessage />
                <div className="text-xs text-muted-foreground mt-1">
                  {field.value?.length || 0}/100 characters
                </div>
              </FormItem>
            )}
          />

          {/* Prayer Content - REQUIRED */}
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  Prayer Request <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    maxLength={2000}
                    className="min-h-[120px] bg-background border-border text-foreground resize-none"
                    placeholder="Share your prayer request in detail. Let our community know how they can pray for you..."
                    required
                  />
                </FormControl>
                <FormMessage />
                <div className="text-xs text-muted-foreground mt-1">
                  {field.value?.length || 0}/2000 characters
                </div>
              </FormItem>
            )}
          />

          {/* Category Selection (optional) */}
          <CategorySelector
            control={form.control}
            categories={categories}
            onAddCategory={handleAddCategory}
          />

          {/* Privacy Settings (optional) */}
          <FormField
            control={form.control}
            name="visibility"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  Privacy Setting (optional)
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background border-border text-foreground">
                      <SelectValue placeholder="Choose who can see your prayer" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-background border border-border z-[200] max-h-[40dvh] overflow-y-auto">
                    <SelectItem value="public" className="hover:bg-accent">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">Public</span>
                        <span className="text-xs text-muted-foreground">
                          Visible to all community members
                        </span>
                      </div>
                    </SelectItem>
                    <SelectItem value="private" className="hover:bg-accent">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">Private</span>
                        <span className="text-xs text-muted-foreground">
                          Only visible to you
                        </span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Form Actions */}
          {showActions && (
            <FormActions isSubmitting={isSubmitting} onCancel={onCancel} />
          )}

          {/* Encouragement Message */}
          <EncouragementMessage />
        </form>
      </Form>
    </div>
  );
}
